import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import AppAuth from 'AppAuth';
import AppFetch from 'fetch/AppFetch';
import { UserInfo } from 'appTypes';

/**
 * React Query hook for user info with automatic profile change detection
 *
 * Features:
 * - Refetches on window focus when data is stale (5+ minutes old)
 * - Refetches on component mount when data is stale (5+ minutes old)
 * - Detects Zywave profile switches and invalidates all queries
 * - Keeps localStorage in sync (for legacy code compatibility)
 *
 * Cache Settings:
 * - staleTime: 5 minutes (reduces API calls since user info rarely changes)
 * - gcTime: 10 minutes (keeps data in memory)
 * - No background polling (relies on user activity for profile detection)
 *
 * @returns React Query result with user info data
 */
export const useUserInfo = () => {
  const queryClient = useQueryClient();
  const lastProfileIdRef = useRef<string | null | undefined>(null);

  const { data, isLoading, isError, error, ...rest } = useQuery<UserInfo | null>({
    queryKey: ['userInfo'],
    queryFn: async () => {
      // Fetch fresh user info from API
      // This internally calls LoginFetch.findUserInfo() which:
      // 1. Fetches from backend with cache-busting
      // 2. For Zywave users, fetches current profile from Zywave API
      // 3. Updates localStorage via AppStore.storeUserInfo()
      await AppAuth.getUserInfo();

      // Return from localStorage (source of truth for legacy code)
      return AppFetch.fetchUserInfo();
    },
    staleTime: 300000, // 5 minutes - user info rarely changes
    gcTime: 600000, // 10 minutes - keep in cache longer
    refetchOnWindowFocus: true, // Refetch when user returns to tab (respects staleTime)
    refetchOnMount: true, // Refetch on component mount (respects staleTime)
    refetchInterval: false, // No polling - rely on user activity for profile change detection
    retry: 3, // Retry failed requests up to 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * attemptIndex, 30000),
  });

  // Detect profile changes for Zywave users
  useEffect(() => {
    if (!data || data.account_type !== 7) {
      return;
    }

    const currentProfileId = data.zywave_profile_id;

    // Check if profile has changed since last check
    if (lastProfileIdRef.current && lastProfileIdRef.current !== currentProfileId) {
      // Invalidate all queries to force refetch with new profile context
      queryClient.invalidateQueries();

      // Reload page to ensure all legacy components get fresh data
      // This is necessary because legacy code reads from localStorage at mount
      // and doesn't react to changes
      window.location.reload();
    }

    lastProfileIdRef.current = currentProfileId;
  }, [data, queryClient]);

  return { data, isLoading, isError, error, ...rest };
};
